import { computed, ref, watch, watchEffect } from "vue";
import { defineStore } from "pinia";
import { WagerBoard } from "@sazka/lotteries/js/models/ds-online/wager-board";
import { Lottery } from "@sazka/lotteries/js/models/lottery";
import { BetDetail } from "@sazka/lotteries/js/models/my-tickets/bet-detail";
import { Rychla6BetType } from "@sazka/lotteries/js/models/rychla-6-bet-type";
import { SubscriptionDetail } from "@sazka/lotteries/js/models/subscriptions/subscription-detail";
import { FtDetailDto } from "@sazka/lotteries/js/my-tickets/favotire-tickets/models/ft-models";
import { UrlUtils } from "@sazka/utils/url";
import { QuickWagerCard } from "@/lotteries/category-page/model/quick-wager-card";
import { QuickPickWager } from "@/lotteries/model/quick-pick-wager";
import usePpBetAnalytics from "../composables/use-pp-bet-analytics";
import { usePpTicketCheck } from "../composables/use-ticket-check";
import { usePpWagerConfiguration } from "../composables/use-wager-configuration";
import { usePpWagerUtils } from "../composables/use-wager-utils";

export const useWagerBoardStore = () => {
    const config = usePpWagerConfiguration().config;
    const wagerUtils = usePpWagerUtils();
    const { boardsFillTracking } = usePpBetAnalytics();

    const { lottery } = config;
    const ticketCheck = usePpTicketCheck().isTicketCheck.value;

    if (!lottery) {
        console.error("Wager configuration is not set. Please configure using usePpWagerConfiguration.");
        return;
    }

    const storeId = `wager-store-${lottery}` + (ticketCheck ? "-ticket-check" : "");

    const store = defineStore(
        storeId,
        () => {
            /** State */
            const boards = ref<WagerBoard[]>([]);
            const systemBoard = ref<WagerBoard>(wagerUtils.newSystemWagerBoard());
            const boardIndex = ref(0);
            const addonNumbers = ref<number[]>([]);
            const isAddonPlayed = ref(config.addonNumbers > 0);
            const isSystem = ref(false);
            const isSystemSelected = ref(false);
            const multiplier = ref(1);
            const wagerStakes = ref<number[]>([]);
            const pastDrawId = ref(-1);
            const isTicketCheck = computed(() => ticketCheck);
            const maxBoardCount = ref(config.boardCount);
            const initialBoardCount = ref(isTicketCheck.value ? 1 : config.boardCount);
            const isBoardCountDirty = ref(false);

            /** Allows to safely get and set the board count */
            const boardCount = computed({
                get: () => boards.value.length,
                set: setBoardCount,
            });

            const getBoardsForCheckout = computed(() => {
                if (lottery === Lottery.rychla6) {
                    return boards.value.filter((board) => board.primary.length > 0);
                }

                return boards.value;
            });

            /** Lottery specific */

            // kasicka
            const kasickaStakeTable = ref<boolean[][]>([...Array(5)].map(() => Array(6).fill(false)));
            /** Accumulate kasicka stakes */
            if (lottery === Lottery.kasicka && isTicketCheck.value) {
                watchEffect(() => {
                    const stakeValues = kasickaStakeTable.value.map((row) => {
                        return row.reduce((total, isChecked, index) => {
                            if (!isChecked) return total;
                            return total + (index === 5 ? 10 : index + 1);
                        }, 0);
                    });

                    // Always send array of 5 stakes - pad with 0s if needed
                    const primaryLength = systemBoard.value.primary.length;
                    const paddingLength = Math.max(5 - primaryLength, 0);
                    const paddedValues = stakeValues.toSpliced(primaryLength, 5, ...Array(paddingLength).fill(0));

                    wagerStakes.value = paddedValues;
                });
            }

            // extra renta
            const extraRentaStake = computed({
                get: () => {
                    if (lottery !== Lottery.extraRenta) {
                        return null;
                    }
                    return wagerStakes.value[0];
                },
                set: (value) => {
                    if (lottery !== Lottery.extraRenta) {
                        return;
                    }
                    wagerStakes.value[0] = value;
                },
            });

            if (lottery === Lottery.extraRenta) {
                const handleStakeChange = () => {
                    boards.value.every((board) => {
                        board.stake = extraRentaStake.value;
                        return true;
                    });
                };

                watch(
                    [boardCount, extraRentaStake],
                    () => {
                        handleStakeChange();
                    },
                    { immediate: true }
                );
            }

            /* System Wager */
            const systemPrimaryCount = computed({
                get: () => (isTicketCheck.value ? config.primarySystemMaxCount : systemBoard.value?.primaryCount),
                set: (value) => {
                    systemBoard.value.primaryCount = value;
                    systemBoard.value.primary = systemBoard.value.primary.slice(0, value);
                },
            });

            const systemSecondaryCount = computed({
                get: () => (isTicketCheck.value ? config.secondarySystemMaxCount : systemBoard.value?.secondaryCount),
                set: (value) => {
                    systemBoard.value.secondaryCount = value;
                    systemBoard.value.secondary = systemBoard.value.secondary.slice(0, value);
                },
            });

            const isBetSuccessfull = ref(false);

            const isBoardFilled = (index: number) => {
                return boards.value[index] && wagerUtils.isWagerBoardFilled(boards.value[index]);
            };

            const isWagerFilledRychla6 = () => {
                const isMainGameValid =
                    boards.value[0].primary.length === 0 || boards.value[0].primary.length === config.primaryBoardSize;

                return isMainGameValid && boards.value.some((board) => wagerUtils.isWagerBoardFilled(board));
            };

            const isWagerFilled = computed(() => {
                if (lottery === Lottery.rychla6) {
                    return isWagerFilledRychla6();
                }

                if (isSystem.value && isTicketCheck.value)
                    return wagerUtils.isSystemTicketCheckBoardFilled(systemBoard.value);
                else if (isSystem.value) return wagerUtils.isWagerBoardFilled(systemBoard.value);

                return boards.value.every((board) => wagerUtils.isWagerBoardFilled(board));
            });

            const isTicketCheckValid = computed(() => {
                if (!isSystem.value) {
                    return isWagerFilled.value && wagerUtils.isAddonValid(addonNumbers.value);
                }

                return (
                    wagerUtils.isSystemTicketCheckBoardFilled(systemBoard.value) &&
                    wagerUtils.isAddonValid(addonNumbers.value)
                );
            });

            /* Actions */
            function randomizeBoard(index?: number) {
                if (isSystem.value) {
                    systemBoard.value = wagerUtils.randomizeWagerBoard(systemBoard.value);
                } else if (index !== undefined) {
                    const board = getBoard(index);

                    const randomizedBoard = wagerUtils.randomizeWagerBoard(board);

                    boards.value[index] = randomizedBoard;

                    boardsFillTracking.value[index] = "randomColumn";
                } else {
                    console.error("Board index for randomization is undefined.");
                    return;
                }
            }

            function randomizeWager(withAddon?: boolean) {
                if (isSystem.value) {
                    randomizeBoard();
                    return;
                }

                boardCount.value = maxBoardCount.value;
                boards.value = boards.value.map((board) => wagerUtils.randomizeWagerBoard(board));

                boardsFillTracking.value = Array(boardCount.value).fill("randomFull");

                if (withAddon) {
                    isAddonPlayed.value = true;
                    randomizeAddon();
                }
            }

            function addBoard() {
                if (boardCount.value < maxBoardCount.value) {
                    boardCount.value += 1;
                    boardIndex.value = boardCount.value - 1;
                }
            }

            function resetKasickaStakeTable() {
                if (lottery === Lottery.kasicka)
                    kasickaStakeTable.value = [...Array(5)].map(() => Array(6).fill(false));
            }

            function deleteBoard(index?: number) {
                multiplier.value = 1;

                if (isSystem.value) {
                    systemBoard.value = wagerUtils.newSystemWagerBoard();
                    resetKasickaStakeTable();
                    return;
                }

                if (boardCount.value === 1) {
                    boards.value[0] = wagerUtils.newWagerBoard();
                    return;
                }

                boards.value.splice(index, 1);
            }

            function getAddonNum(index: number) {
                return addonNumbers.value[index];
            }

            function setAddonNum(index: number, num: number) {
                addonNumbers.value[index] = num;
            }

            function randomizeAddon() {
                if (config.addonNumbers) addonNumbers.value = wagerUtils.randomizeAddon(lottery);
            }

            function clearBoard(index?: number) {
                if (isSystem.value) {
                    systemBoard.value = wagerUtils.clearWagerBoard(systemBoard.value);
                } else if (index !== undefined) {
                    const board = getBoard(index);

                    const clearedBoard = wagerUtils.clearWagerBoard(board);

                    boards.value[index] = clearedBoard;
                } else {
                    console.error("Board index for clearing is undefined.");
                    return;
                }
            }

            function clearWager() {
                boards.value = [];
                boardIndex.value = 0;
                extraRentaStake.value = config.defaultStake;
                multiplier.value = 1;

                if (isTicketCheck.value) {
                    addonNumbers.value = [];
                    isAddonPlayed.value = false;
                } else {
                    randomizeAddon();
                    isAddonPlayed.value = config.addonNumbers > 0;
                }

                setBoardCount(initialBoardCount.value);
                isBoardCountDirty.value = false;

                systemBoard.value = wagerUtils.newSystemWagerBoard();
                systemPrimaryCount.value = config.primarySystemDefaultCount ?? config.primarySystemMinCount;
                systemSecondaryCount.value = config.secondarySystemDefaultCount ?? config.secondarySystemMinCount;
            }

            const getBoardIndex = computed(() =>
                Number.isInteger(boardIndex.value) ? boardIndex.value : boards.value.length
            );

            function checkIndex(index: number) {
                if (index < 0 || index >= boardCount.value) {
                    console.error(`Invalid board index: ${index}`);
                    return false;
                }
                return true;
            }

            function switchToStandardBet() {
                // Kasicka has different system bet logic
                if (lottery === Lottery.kasicka) return;

                isSystem.value = false;
                UrlUtils.removeUrlPath("/systemova");
            }

            function setFavoriteWagerRychla6(favoriteWager: FtDetailDto) {
                favoriteWager.numbers.forEach((numbers) => {
                    boards.value[numbers.systemBetType] = wagerUtils.mapFavoriteBoard(numbers);
                });
            }

            function setFavoriteWagerStastneDatum(favoriteWager: FtDetailDto) {
                boards.value[0] = wagerUtils.mapFavoriteBoard(favoriteWager.numbers[0]);

                multiplier.value = favoriteWager.multiplier;
                const stake = favoriteWager.multiplier * config.defaultStake;

                boards.value[0].stake = stake;
            }

            function setFavoriteWagerDefault(favoriteWager: FtDetailDto) {
                isSystem.value = favoriteWager.isSystem;
                isAddonPlayed.value = favoriteWager.addonPlayed;
                if (isAddonPlayed.value) addonNumbers.value = favoriteWager.addonNumbers;
                multiplier.value = favoriteWager.multiplier;
                extraRentaStake.value = favoriteWager.numbers?.[0]?.stake;

                if (isSystem.value) {
                    systemBoard.value = wagerUtils.mapFavoriteSystemBoard(favoriteWager.numbers?.[0], lottery);
                    if (lottery === Lottery.kasicka) {
                        wagerStakes.value = favoriteWager.numbers?.[0].additional;
                    }
                } else {
                    boards.value = favoriteWager.numbers.map((numbers) => wagerUtils.mapFavoriteBoard(numbers));

                    boardCount.value = boards.value.length;
                }
            }

            /**
             * Set the store state from a favorite wager
             *
             * Designed for use in My Tickets
             */
            function setFromFavoriteWager(favoriteWager: FtDetailDto) {
                clearWager();

                if (lottery === Lottery.rychla6) {
                    setFavoriteWagerRychla6(favoriteWager);
                    return;
                } else if (lottery === Lottery.stastneDatum) {
                    // Stastne Datum doesn't have favorite wager, but we implement it for future use
                    setFavoriteWagerStastneDatum(favoriteWager);
                    return;
                }

                setFavoriteWagerDefault(favoriteWager);
            }

            function setBetDetailStastneDatum(betDetail: BetDetail | SubscriptionDetail) {
                boards.value[0] = wagerUtils.mapBetDetailBoard(betDetail.boards[0]);

                multiplier.value = betDetail.multiplier;
                const stake = betDetail.multiplier * config.defaultStake;

                boards.value[0].stake = stake;
            }

            function setBetDetailRychla6(betDetail: BetDetail | SubscriptionDetail) {
                betDetail.boards.forEach((board) => {
                    boards.value[Number(board.systemBetType)] = wagerUtils.mapBetDetailBoard(board);
                });
            }

            function setBetDetailDefault(betDetail: BetDetail | SubscriptionDetail) {
                isSystem.value = betDetail.boards?.[0].betType === "SYSTEM";
                isAddonPlayed.value = !!betDetail.addonNumbers?.length;
                extraRentaStake.value = betDetail.stake;

                if (isAddonPlayed.value) addonNumbers.value = betDetail.addonNumbers?.map((n) => Number(n));
                multiplier.value = betDetail.multiplier;

                if (isSystem.value) {
                    systemBoard.value = wagerUtils.mapBetDetailSystemBoard(betDetail.boards?.[0], lottery);
                    if (lottery === Lottery.kasicka) {
                        wagerStakes.value = betDetail.boards?.[0].secondarySelections?.map((n) => Number(n));
                    }
                } else {
                    boards.value = betDetail.boards.map((numbers) => wagerUtils.mapBetDetailBoard(numbers));

                    boardCount.value = boards.value.length;
                }
            }

            function setFromBetDetail(betDetail: BetDetail | SubscriptionDetail) {
                clearWager();

                if (lottery === Lottery.rychla6) {
                    setBetDetailRychla6(betDetail);
                    return;
                } else if (lottery === Lottery.stastneDatum) {
                    setBetDetailStastneDatum(betDetail);
                    return;
                }

                setBetDetailDefault(betDetail);
            }

            function setRychla6QuickWager(quickWager: QuickPickWager) {
                const boardToSetIndex = Number(quickWager.subGame);
                if (quickWager.subGame === Rychla6BetType.MainGame) {
                    boards.value[boardToSetIndex] = wagerUtils.randomizeWagerBoard(boards.value[boardToSetIndex]);
                } else {
                    const values = [1, 2, 3];
                    boards.value[boardToSetIndex].primary = [values[Math.floor(Math.random() * values.length)]];
                }

                boards.value[boardToSetIndex].stake = quickWager.stake || config.defaultStake;
            }

            function setDefaultQuickWager(quickWager: QuickPickWager) {
                isSystem.value = quickWager.isCombinedGame ?? false;
                isAddonPlayed.value = quickWager.isAddonPlayed;
                randomizeAddon();
                multiplier.value = quickWager.multiplier;
                wagerStakes.value = [];
                if (quickWager.combinedStakes) {
                    wagerStakes.value = quickWager.combinedStakes;
                } else if (quickWager.stake) {
                    wagerStakes.value = [quickWager.stake];
                }

                boardCount.value = quickWager.columns;

                if (isSystem.value) {
                    systemPrimaryCount.value = quickWager.numbers;
                    systemBoard.value = wagerUtils.randomizeWagerBoard(systemBoard.value);
                } else {
                    if (quickWager.numbers || quickWager.isKingsGame || quickWager.stake) {
                        boards.value.forEach((board) => {
                            board.primaryCount = quickWager.numbers;
                            board.stake = quickWager.stake;
                            board.kralovskaHra = quickWager.isKingsGame;
                        });
                    }

                    if (quickWager.figures?.length) {
                        const numbers = quickWager.figures.map((f) => Number(f));
                        boards.value.forEach((board) => {
                            board.primary = numbers;
                        });
                    } else {
                        boards.value = boards.value.map((board) => wagerUtils.randomizeWagerBoard(board));
                    }

                    boardCount.value = boards.value.length;
                }
            }

            function setFromQuickWager(quickWagerCard: QuickWagerCard) {
                clearWager();

                if (lottery === Lottery.rychla6) {
                    setRychla6QuickWager(quickWagerCard.wager);
                    return;
                }

                setDefaultQuickWager(quickWagerCard.wager);
            }

            function setFromStatistics(primaryNumbers?: number[], secondaryNumbers?: number[]) {
                clearWager();
                isSystem.value = false;
                setBoardCount(1);

                const primaryCount = lottery === Lottery.kasicka ? primaryNumbers.length + 1 : primaryNumbers.length;
                setBoardPrimaryCount(0, primaryCount);
                boards.value[0].primary = [...primaryNumbers];

                if (secondaryNumbers?.length) {
                    setBoardSecondaryCount(0, secondaryNumbers.length);
                    boards.value[0].secondary = [...secondaryNumbers];
                }
            }

            function setBoard(index: number, board: WagerBoard) {
                if (!checkIndex(index)) return;
                boards.value[index] = board;
            }

            function getBoard(index: number) {
                return boards.value[index];
            }

            function setBoardPrimaryCount(index: number, count: number) {
                const hasFixedBoardCount =
                    !config.primaryMinBoardSize || config.primaryMinBoardSize == config.primaryBoardSize;

                if (hasFixedBoardCount || !checkIndex(index)) return;
                boards.value[index].primaryCount = count;

                if (count < boards.value[index].primary.length) {
                    boards.value[index].primary = boards.value[index].primary.slice(0, count);
                }
            }

            function setBoardSecondaryCount(index: number, count: number) {
                if (!checkIndex(index)) return;
                boards.value[index].secondaryCount = count;

                if (count < boards.value[index].secondary.length) {
                    boards.value[index].secondary = boards.value[index].secondary.slice(0, count);
                }
            }

            function setBoardCount(count: number) {
                if (lottery === Lottery.rychla6) {
                    count = 3;
                }

                if (count < 1) {
                    count = 1;
                }

                if (count > maxBoardCount.value) {
                    count = maxBoardCount.value;
                }

                if (count < boards.value.length) {
                    boards.value = boards.value.slice(0, count);
                }

                if (count > boards.value.length) {
                    const newBoards = Array.from({ length: count - boards.value.length }, () =>
                        wagerUtils.newWagerBoard()
                    );
                    boards.value = [...boards.value, ...newBoards];
                }
                isBoardCountDirty.value = true;

                setRychla6BoardTypes();
            }

            function setInitialBoardCount(count: number) {
                initialBoardCount.value = count;
                if (!isBoardCountDirty.value) setBoardCount(count);
            }

            function setBoardIndex(index: number) {
                if (!checkIndex(index)) return;
                boardIndex.value = index;
            }

            function generateBoards(count: number) {
                boards.value = Array.from({ length: count }, () => wagerUtils.randomizeWagerBoard());
            }

            function restoreBoards(boardsToRestore: WagerBoard[]) {
                clearWager();
                boards.value = boardsToRestore;
            }

            function setRychla6BoardTypes() {
                if (config.lottery !== Lottery.rychla6) return;

                const indexedBoardTypes = [Rychla6BetType.MainGame, Rychla6BetType.EvenOdd, Rychla6BetType.LowHigh];

                boards.value.forEach((board, index) => {
                    board.betType = indexedBoardTypes[index];
                });
            }

            function isAnyBoardFilled() {
                return (
                    boards.value.length != initialBoardCount.value ||
                    boards.value.some(
                        (board) => board.primary.length || board.secondary.length || board.tertiary.length
                    )
                );
            }

            const sortBoard = (boardToSort: WagerBoard) => {
                boardToSort.primary = boardToSort.primary.sort((a, b) => a - b);
                if (boardToSort.secondary) boardToSort.secondary = boardToSort.secondary?.sort((a, b) => a - b);
                if (boardToSort.tertiary) boardToSort.tertiary = boardToSort.tertiary?.sort((a, b) => a - b);

                return boardToSort;
            };

            const boardsSorted = computed(() => {
                boards.value.forEach((board) => {
                    boards.value[boards.value.indexOf(board)] = sortBoard(board);
                });
                return boards.value;
            });
            /* ----- */

            /* Watchers */
            watch(
                boardCount,
                (boardCountValue) => {
                    if (boardCountValue > maxBoardCount.value) {
                        boardCount.value = maxBoardCount.value;
                    }

                    if (boardIndex.value + 1 >= boardCountValue) {
                        boardIndex.value = boardCountValue - 1;
                        if (boardIndex.value < 0) boardIndex.value = 0;
                    }

                    if (boardCountValue < boardsFillTracking.value.length) {
                        boardsFillTracking.value = boardsFillTracking.value.slice(0, boardCountValue);
                    }

                    if (boardCountValue > boardsFillTracking.value.length) {
                        const newTrackingItems = Array(boardCountValue - boardsFillTracking.value.length).fill(
                            "manual"
                        );
                        boardsFillTracking.value = [...boardsFillTracking.value, ...newTrackingItems];
                    }
                },
                { immediate: true }
            );

            watch(isSystem, (isSystemValue) => {
                if (isSystemValue) {
                    isSystemSelected.value = true;
                }
            });

            watch(maxBoardCount, (maxBoardCountValue) => {
                if (boardCount.value > maxBoardCountValue) {
                    boardCount.value = maxBoardCountValue;
                }
            });
            /* ----- */
            function initialize() {
                clearWager();
            }

            initialize();
            /* ----- */

            /* Expose */
            return {
                boardCount,
                boards,
                boardIndex,
                addonNumbers,
                isAddonPlayed,
                multiplier,
                isSystem,
                isSystemSelected,
                systemBoard,
                systemPrimaryCount,
                systemSecondaryCount,
                isTicketCheck,
                isTicketCheckValid,
                maxBoardCount,
                pastDrawId,
                isBoardFilled,
                randomizeBoard,
                getBoardIndex,
                setBoard,
                getBoard,
                isWagerFilled,
                randomizeWager,
                wagerStakes,
                clearBoard,
                clearWager,
                addBoard,
                deleteBoard,
                switchToStandardBet,
                setFromFavoriteWager,
                setFromBetDetail,
                setFromQuickWager,
                setFromStatistics,
                setBoardPrimaryCount,
                setBoardSecondaryCount,
                generateBoards,
                restoreBoards,
                setBoardIndex,
                setBoardCount,
                setInitialBoardCount,
                getAddonNum,
                setAddonNum,
                randomizeAddon,
                isAnyBoardFilled,
                boardsSorted,
                isBoardCountDirty,
                isBetSuccessfull,
                kasickaStakeTable,
                extraRentaStake,
                getBoardsForCheckout,
            };
            /* ----- */
        },
        {
            persist: {
                // eslint-disable-next-line no-restricted-globals
                storage: ticketCheck ? localStorage : sessionStorage,
            },
        }
    );

    return store();
};
